import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { Plus, Search, Trash2, Folder, Calendar, User, BarChart3 } from 'lucide-react';
import ProjectFormModal from '../components/ProjectFormModal';

const ExperimentProjects = () => {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const navigate = useNavigate();

    const [currentUser, setCurrentUser] = useState(null);

    useEffect(() => {
        const getCurrentUser = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            setCurrentUser(user);
        };
        getCurrentUser();
        fetchProjects();

        // Set up real-time subscription
        // We listen to ALL events on the experiments table and filter locally
        // This ensures we catch DELETE events even if the server filter fails
        const channel = supabase
            .channel('projects-changes')
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'experiments'
                },
                (payload) => {
                    if (payload.eventType === 'INSERT' && payload.new.type === 'project') {
                        setProjects((prev) => {
                            if (prev.some(p => p.id === payload.new.id)) return prev;
                            return [payload.new, ...prev];
                        });
                    } else if (payload.eventType === 'UPDATE' && payload.new.type === 'project') {
                        setProjects((prev) =>
                            prev.map((p) => (p.id === payload.new.id ? payload.new : p))
                        );
                    } else if (payload.eventType === 'DELETE') {
                        // For DELETE, we just check if the deleted ID exists in our current list
                        // This works regardless of whether payload.old has the 'type' field
                        setProjects((prev) => prev.filter((p) => p.id !== payload.old.id));
                    }
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, []);

    const fetchProjects = async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('experiments')
                .select('*')
                .eq('type', 'project')
                .order('created_at', { ascending: false });

            if (error) throw error;
            setProjects(data || []);
        } catch (error) {
            console.error('Error fetching projects:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (e, id) => {
        e.stopPropagation();
        if (!window.confirm('Are you sure you want to delete this project? All associated experiments will also be deleted.')) {
            return;
        }

        try {
            // Optimistic update
            setProjects((prev) => prev.filter((p) => p.id !== id));

            const { error } = await supabase.from('experiments').delete().eq('id', id);
            if (error) {
                // Revert if error
                fetchProjects();
                throw error;
            }
        } catch (error) {
            console.error('Error deleting project:', error);
            alert('Error deleting project: ' + error.message);
        }
    };

    const filteredProjects = projects.filter((p) => {
        const query = searchQuery.toLowerCase();
        return (
            p.title?.toLowerCase().includes(query) ||
            p.description?.toLowerCase().includes(query) ||
            p.creator_name?.toLowerCase().includes(query)
        );
    });

    const formatDateTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="h-full flex flex-col bg-[#0a0e1a]">
            {/* Header */}
            <div className="bg-[#1a1f2e] border-b border-white/10">
                <div className="max-w-7xl mx-auto px-3 lg:px-4 py-3 lg:py-4">
                    <div className="flex justify-between items-center gap-2">
                        <div className="min-w-0 flex-1">
                            <h1 className="text-lg lg:text-xl font-bold text-white mb-0.5 lg:mb-1 truncate">
                                Research Projects
                            </h1>
                            <p className="text-gray-400 text-xs lg:text-sm truncate">
                                Organize your experiments into focused research projects.
                            </p>
                        </div>
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="flex items-center gap-1.5 lg:gap-2 bg-indigo-600 text-white px-3 lg:px-4 py-1.5 lg:py-2 rounded-lg hover:bg-indigo-700 transition-colors shadow-sm text-sm lg:text-base flex-shrink-0"
                        >
                            <Plus size={16} className="lg:w-[18px] lg:h-[18px]" />
                            <span className="hidden sm:inline">New Project</span>
                            <span className="sm:hidden">New</span>
                        </button>
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto scrollbar-thin">
                <div className="max-w-7xl mx-auto px-4 py-6">
                    {/* Search Bar */}
                    <div className="relative mb-6">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                        <input
                            type="text"
                            placeholder="Search projects..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full max-w-md pl-9 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm text-white placeholder-gray-500 transition-all"
                        />
                    </div>

                    {/* Projects Grid */}
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-20">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mb-4"></div>
                            <p className="text-gray-400">Loading projects...</p>
                        </div>
                    ) : filteredProjects.length === 0 ? (
                        <div className="bg-[#1a1f2e] rounded-xl p-12 text-center border border-white/10">
                            <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Folder className="text-gray-500" size={32} />
                            </div>
                            <h3 className="text-xl font-semibold text-white mb-2">No projects found</h3>
                            <p className="text-gray-400 mb-6">
                                {searchQuery
                                    ? "We couldn't find any projects matching your search."
                                    : "Start by creating your first research project to organize your experiments."}
                            </p>
                            {!searchQuery && (
                                <button
                                    onClick={() => setIsModalOpen(true)}
                                    className="inline-flex items-center gap-2 bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
                                >
                                    <Plus size={18} />
                                    Create Project
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filteredProjects.map((project) => (
                                <div
                                    key={project.id}
                                    className="group bg-[#1a1f2e] rounded-xl border border-white/10 p-6 hover:border-indigo-500/50 hover:shadow-lg hover:shadow-indigo-500/10 transition-all relative overflow-hidden"
                                >
                                    <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity"></div>

                                    <div className="flex justify-between items-start mb-4">
                                        <div className="p-2 bg-indigo-500/10 rounded-lg text-indigo-400">
                                            <Folder size={24} />
                                        </div>
                                        {currentUser && project.created_by === currentUser.id && (
                                            <button
                                                onClick={(e) => handleDelete(e, project.id)}
                                                className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                                title="Delete Project"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        )}
                                    </div>

                                    <h3 className="text-xl font-bold text-white mb-2 group-hover:text-indigo-400 transition-colors">
                                        {project.title || project.name}
                                    </h3>

                                    <p className="text-gray-400 text-sm mb-6 line-clamp-2 min-h-[40px]">
                                        {project.description || 'No description provided.'}
                                    </p>

                                    <div className="space-y-3 pt-4 border-t border-white/5">
                                        <div className="flex items-center gap-2 text-xs text-gray-400">
                                            <Calendar size={14} />
                                            <span>{formatDateTime(project.created_at)}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-xs text-gray-400">
                                            <User size={14} />
                                            <span>Created by <span className="text-gray-300 font-medium">{project.creator_name}</span></span>
                                        </div>
                                        <div className="flex gap-2 mt-3">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/experiment-tracking/${project.id}`);
                                                }}
                                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors text-sm font-medium"
                                            >
                                                <Folder size={16} />
                                                View Experiments
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/project-detail/${project.id}`);
                                                }}
                                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600/20 hover:bg-indigo-600/30 text-indigo-400 rounded-lg transition-colors text-sm font-medium"
                                            >
                                                <BarChart3 size={16} />
                                                Dashboard
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <ProjectFormModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={fetchProjects}
            />
        </div>
    );
};

export default ExperimentProjects;
